import { useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";
import { Container } from "@mui/material";
import HeaderTemplate from "../home/HeaderTemplate";
import FooterTemplate from "../home/FooterTemplate";
import {
  termsOfServiceIntro,
  termsOfServiceSections,
  type TermsBlock,
} from "./termsOfServiceContent";

const ListDot = () => (
  <span
    aria-hidden="true"
    className="inline-flex h-7 w-3 shrink-0 items-center justify-center self-start"
  >
    <span className="size-1.5 shrink-0 rounded-full bg-black" />
  </span>
);

const renderBlock = (block: TermsBlock, index: number) => {
  switch (block.type) {
    case "paragraph":
      return (
        <p
          key={index}
          className="m-0 text-sm leading-7 text-(--desaturateddarkblue) md:text-base md:leading-7"
        >
          {block.text}
        </p>
      );
    case "subheading":
      return (
        <h3
          key={index}
          className="m-0 mt-2 text-base font-semibold"
        >
          {block.text}
        </h3>
      );
    case "list":
      return (
        <ul key={index} className="m-0 list-none space-y-2 p-0">
          {block.items.map((item) => (
            <li
              key={item}
              className="flex items-start gap-3 text-sm leading-7 text-(--desaturateddarkblue) md:text-base md:leading-7"
            >
              <ListDot />
              <span className="min-w-0 flex-1">{item}</span>
            </li>
          ))}
        </ul>
      );
    case "linkParagraph":
      return (
        <p
          key={index}
          className="m-0 text-sm leading-7 text-(--desaturateddarkblue) md:text-base md:leading-7"
        >
          {block.before}
          <a
            href={block.href}
            className="font-medium text-(--darkblue) underline! decoration-(--darkblue) underline-offset-2"
          >
            {block.linkText}
          </a>
          {block.after}
        </p>
      );
    default:
      return null;
  }
};

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState(
    termsOfServiceSections[0]?.id ?? ""
  );
  const activeSectionRef = useRef(activeSection);
  const isClickScrolling = useRef(false);
  const scrollUnlockTimer = useRef<number>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const headerOffset = 196;
    const switchBuffer = 120;

    const getVisibleHeight = (element: HTMLElement) => {
      const rect = element.getBoundingClientRect();
      const visibleTop = Math.max(rect.top, headerOffset);
      const visibleBottom = Math.min(rect.bottom, window.innerHeight);
      return Math.max(0, visibleBottom - visibleTop);
    };

    const getSectionFromScroll = () => {
      let currentId = termsOfServiceSections[0]?.id ?? "";
      let largestVisible = 0;

      termsOfServiceSections.forEach((section) => {
        const element = document.getElementById(section.id);
        if (!element) {
          return;
        }

        const visibleHeight = getVisibleHeight(element);
        if (visibleHeight > largestVisible) {
          largestVisible = visibleHeight;
          currentId = section.id;
        }
      });

      const activeElement = document.getElementById(activeSectionRef.current);
      if (activeElement) {
        const activeVisible = getVisibleHeight(activeElement);
        if (activeVisible + switchBuffer >= largestVisible) {
          return activeSectionRef.current;
        }
      }

      return currentId;
    };

    const syncActiveSection = () => {
      if (isClickScrolling.current) {
        return;
      }

      const nextId = getSectionFromScroll();
      if (!nextId || nextId === activeSectionRef.current) {
        return;
      }

      activeSectionRef.current = nextId;
      setActiveSection(nextId);
    };

    let frame = 0;
    let debounceTimer = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        window.clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(syncActiveSection, 80);
      });
    };

    syncActiveSection();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(debounceTimer);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    return () => window.clearTimeout(scrollUnlockTimer.current);
  }, []);

  const handleSectionClick = (
    event: MouseEvent<HTMLAnchorElement>,
    sectionId: string
  ) => {
    event.preventDefault();
    const section = document.getElementById(sectionId);
    if (!section) {
      return;
    }

    isClickScrolling.current = true;
    activeSectionRef.current = sectionId;
    setActiveSection(sectionId);
    section.scrollIntoView({ behavior: "smooth", block: "start" });

    const unlockHighlight = () => {
      isClickScrolling.current = false;
      window.removeEventListener("scrollend", unlockHighlight);
      window.clearTimeout(scrollUnlockTimer.current);
    };

    window.clearTimeout(scrollUnlockTimer.current);
    window.addEventListener("scrollend", unlockHighlight, { once: true });
    scrollUnlockTimer.current = window.setTimeout(unlockHighlight, 1200);
  };

  return (
    <main className="min-h-screen bg-(--verywhiteblue)">
      <HeaderTemplate variant="page" />

      <section className="pt-[120px] pb-10 max-[575.98px]:pt-[92px] sm:pb-14 md:pt-[132px] lg:pt-[140px]">
        <Container maxWidth="xl">
          <section className="relative mb-6 overflow-hidden rounded-2xl border border-[#e3e7f3] bg-white px-4 py-5 shadow-[0_4px_12px_rgba(4,30,121,0.04)] sm:rounded-[28px] sm:px-6 sm:py-6 md:px-8 md:py-7">
            <div className="pointer-events-none absolute top-1 right-0 h-56 w-56 rounded-full bg-[radial-gradient(circle,rgba(4,30,121,0.04)_0%,transparent_70%)]" />

            <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
              <div className="min-w-0 flex-1">
                <span className="inline-flex w-max items-center gap-2 rounded-full border border-[#e3e7f3] bg-[#e5ebff] px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.16em]">
                  Legal Notice
                </span>
                <h1 className="m-0 mt-4 text-2xl font-semibold leading-tight sm:text-3xl md:text-4xl">
                  Application Terms of Service
                </h1>
                <p className="m-0 mt-3 max-w-3xl text-sm leading-normal text-(--desaturateddarkblue) sm:text-base">
                  {termsOfServiceIntro}
                </p>
              </div>

              <div className="hidden shrink-0 lg:flex">
                <svg
                  viewBox="0 0 120 120"
                  className="h-28 w-28"
                  aria-hidden
                >
                  <circle cx="60" cy="60" r="56" fill="#e5ebff" />

                  {/* Contract document */}
                  <path
                    d="M36 28H72L84 40V92C84 95.3 81.3 98 78 98H42C38.7 98 36 95.3 36 92V28Z"
                    fill="var(--darkblue)"
                    fillOpacity="0.92"
                  />
                  <path
                    d="M72 28V40H84"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                    opacity="0.35"
                  />

                  {/* Clause lines */}
                  <path
                    d="M46 50H74M46 60H74M46 70H66"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    opacity="0.9"
                  />

                  {/* Signature stroke */}
                  <path
                    d="M46 84C50 80 54 86 58 82C60 80 62 78 66 82"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity="0.85"
                  />

                  {/* Agreement seal badge */}
                  <circle cx="82" cy="82" r="16" fill="#e5ebff" />
                  <circle
                    cx="82"
                    cy="82"
                    r="11"
                    fill="none"
                    stroke="var(--darkblue)"
                    strokeWidth="2.2"
                  />
                  <path
                    d="M76 82.5L80 86.5L89 76.5"
                    fill="none"
                    stroke="var(--darkblue)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </section>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_minmax(0,1fr)] xl:grid-cols-[280px_minmax(0,1fr)]">
            <aside>
              <nav
                aria-label="Terms of service sections"
                className="rounded-2xl border border-[#e3e7f3] bg-white p-4 shadow-[0_4px_12px_rgba(4,30,121,0.04)] sm:p-5 lg:sticky lg:top-36 lg:max-h-[calc(100vh-10.5rem)] lg:overflow-y-auto"
              >
                <p className="m-0 mb-3 text-xs font-semibold uppercase tracking-[0.16em]">
                  On this page
                </p>
                <div className="flex flex-wrap gap-2 lg:flex-col lg:gap-1">
                  {termsOfServiceSections.map((section, index) => {
                    const isActive = activeSection === section.id;

                    return (
                      <a
                        key={section.id}
                        href="/terms-of-service"
                        onClick={(event) =>
                          handleSectionClick(event, section.id)
                        }
                        className={`inline-flex h-8 shrink-0 items-center gap-2 rounded-full border px-3 text-xs font-semibold leading-none no-underline lg:h-10 lg:w-full lg:rounded-xl lg:px-3 lg:text-sm ${
                          isActive
                            ? "bg-(--darkblue) text-white"
                            : "text-(--darkblue) hover:bg-[#e5ebff] border-transparent"
                        }`}
                      >
                        <span
                          className={`hidden h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold lg:inline-flex ${
                            isActive
                              ? "bg-white/20 text-white"
                              : "bg-[#e5ebff] text-(--darkblue)"
                          }`}
                        >
                          {index + 1}
                        </span>
                        {section.navTitle}
                      </a>
                    );
                  })}
                </div>
              </nav>
            </aside>

            <article className="overflow-hidden rounded-2xl border border-[#e3e7f3] bg-white shadow-[0_4px_12px_rgba(4,30,121,0.04)] sm:rounded-[24px]">
              {termsOfServiceSections.map((section, index) => (
                <section
                  key={section.id}
                  id={section.id}
                  className="scroll-mt-48 border-b border-[#e3e7f3] px-4 py-5 last:border-b-0 sm:px-6 sm:py-6 md:px-8 md:py-7"
                >
                  <div className="mb-4 flex items-center gap-3">
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#e5ebff] text-xs font-semibold leading-none">
                      {index + 1}
                    </span>
                    <h2 className="m-0 text-lg font-semibold sm:text-xl">
                      {section.title}
                    </h2>
                  </div>
                  <div className="flex flex-col gap-3">
                    {section.blocks.map(renderBlock)}
                  </div>
                </section>
              ))}
            </article>
          </div>
        </Container>
      </section>

      <FooterTemplate />
    </main>
  );
};

export default TermsOfService;
